import { Component, ElementRef, computed, inject, output, input, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertService,
  AlertType,
  GenericModalComponent,
  LoaderService,
  ModalButtonConfig,
  ModalMode,
} from '@solcre-org/core-ui';
import { BookingService } from '../../../shared/services/booking.service';
import {
  BookingImportIssue,
  BookingImportSuccess,
  BookingImportValidationResponse,
} from '../../../shared/models/booking-import.model';

type BookingImportStatus =
  | 'idle'
  | 'ready'
  | 'uploading'
  | 'success'
  | 'validation_error'
  | 'request_error';

@Component({
  selector: 'app-booking-import-modal',
  imports: [CommonModule, TranslateModule, GenericModalComponent],
  templateUrl: './booking-import-modal.html',
  styleUrls: ['./booking-import-modal.css'],
})
export class BookingImportModal {
  isOpen = input<boolean>(false);
  closed = output<void>();
  imported = output<void>();

  private bookingService = inject(BookingService);
  private loaderService = inject(LoaderService);
  private alertService = inject(AlertService);
  private translateService = inject(TranslateService);

  readonly modalMode = ModalMode.CREATE;

  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  status = signal<BookingImportStatus>('idle');
  selectedFile = signal<File | null>(null);
  importResult = signal<BookingImportSuccess | null>(null);
  validationResponse = signal<BookingImportValidationResponse | null>(null);
  requestErrorMessage = signal<string | null>(null);
  isDragOver = signal<boolean>(false);

  footerButtons = computed<ModalButtonConfig<any>[]>(() => {
    const isSuccess = this.status() === 'success';

    const cancelButton: ModalButtonConfig<any> = {
      text: this.translateService.instant(
        isSuccess ? 'bookings.import.done' : 'bookings.import.cancel'
      ),
      class: 'c-btn--outline',
      action: () => this.onClose(),
    };

    if (isSuccess) {
      return [cancelButton];
    }

    const importButton: ModalButtonConfig<any> = {
      text: this.translateService.instant(
        this.status() === 'uploading' ? 'bookings.import.importing' : 'bookings.import.importButton'
      ),
      class: 'c-btn--primary',
      action: () => this.startImport(),
      disabled: this.status() !== 'ready',
    };

    return [cancelButton, importButton];
  });

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0] ?? null;
    if (file) {
      this.setFile(file);
    }
    target.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    if (this.status() === 'uploading') return;

    const file = event.dataTransfer?.files?.[0] ?? null;
    if (file) {
      this.setFile(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (this.status() === 'uploading') return;
    this.isDragOver.set(true);
  }

  onDragLeave(): void {
    this.isDragOver.set(false);
  }

  openFilePicker(): void {
    if (this.status() === 'uploading') return;
    this.fileInput()?.nativeElement.click();
  }

  downloadTemplate(): void {
    this.loaderService.showLoader();
    this.bookingService.downloadBookingImportTemplate().subscribe({
      next: () => this.loaderService.hideLoader(),
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: err.error?.detail ?? 'errors.generic',
          duration: 3000,
          inBottom: true,
        });
      },
    });
  }

  startImport(): void {
    const file = this.selectedFile();
    if (!file || this.status() !== 'ready') return;

    this.status.set('uploading');
    this.loaderService.showLoader();
    this.bookingService.importBookings(file).subscribe({
      next: (result) => {
        this.loaderService.hideLoader();
        this.importResult.set(result);
        this.status.set('success');
        this.imported.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        if (err.status === 422) {
          this.validationResponse.set(err.error as BookingImportValidationResponse);
          this.status.set('validation_error');
          return;
        }
        this.requestErrorMessage.set(
          err.error?.detail ?? this.translateService.instant('bookings.import.requestErrorFallback')
        );
        this.status.set('request_error');
      },
    });
  }

  onClose(): void {
    this.reset();
    this.closed.emit();
  }

  cellLabel(issue: BookingImportIssue): string {
    if (issue.cell) return issue.cell;
    if (issue.code === 'missing_required_column') {
      return this.translateService.instant('bookings.import.missingColumn');
    }
    return '—';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private static readonly ALLOWED_EXTENSIONS = ['.csv', '.xls', '.xlsx'];

  private setFile(file: File): void {
    const fileName = file.name.toLowerCase();
    if (!BookingImportModal.ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext))) {
      this.alertService.show({
        type: AlertType.ERROR,
        message: this.translateService.instant('bookings.import.invalidFileType'),
        duration: 3000,
        inBottom: true,
      });
      return;
    }

    this.selectedFile.set(file);
    this.importResult.set(null);
    this.validationResponse.set(null);
    this.requestErrorMessage.set(null);
    this.status.set('ready');
  }

  private reset(): void {
    this.status.set('idle');
    this.selectedFile.set(null);
    this.importResult.set(null);
    this.validationResponse.set(null);
    this.requestErrorMessage.set(null);
    this.isDragOver.set(false);
  }
}
